import { applyPalette, GIFEncoder, type Palette, quantize } from 'gifenc';
import type { FrameOutput } from '@meteoradar/shared/contract/frame.schema';
import { createCompositeCanvas, drawComposite, fitSize } from './compose-frame.js';

const MAX_COLORS = 256;
const DISPOSE_KEEP = 1;
const CHANNELS = 4;
const ALPHA = 3;
const OPAQUE = 255;
const PALETTE_SAMPLE_FRAMES = 3;

export type ExportGifOptions = {
  frames: FrameOutput[];
  loadLayers: (frame: FrameOutput) => Promise<ImageBitmap[]>;
  productLabel: string;
  delayMs: number;
  lastDelayMs: number;
  maxSize: number | null;
  onProgress?: (done: number, total: number) => void;
};

const yieldToBrowser = async (): Promise<void> => await new Promise(resolve => setTimeout(resolve, 0));

const differential = (current: Uint8ClampedArray, previous: Uint8ClampedArray): Uint8ClampedArray => {
  const output = new Uint8ClampedArray(current);
  for (let offset = 0; offset < current.length; offset += CHANNELS) {
    if (current[offset] === previous[offset]
      && current[offset + 1] === previous[offset + 1]
      && current[offset + 2] === previous[offset + 2]) {
      output.fill(0, offset, offset + CHANNELS);
    }
  }
  return output;
};

const sampleIndexes = (total: number): number[] => {
  if (total <= PALETTE_SAMPLE_FRAMES) {
    return Array.from({ length: total }, (_, index) => index);
  }
  return [ 0, Math.floor(total / 2), total - 1 ];
};

const concat = (chunks: Uint8ClampedArray[]): Uint8ClampedArray => {
  const output = new Uint8ClampedArray(chunks.reduce((sum, chunk) => sum + chunk.length, 0));
  let offset = 0;
  for (const chunk of chunks) {
    output.set(chunk, offset);
    offset += chunk.length;
  }
  return output;
};

const indexWithTransparency = (data: Uint8ClampedArray, colors: Palette, transparentIndex: number): Uint8Array => {
  const indexes = applyPalette(data, colors);
  for (let pixel = 0, offset = 0; offset < data.length; pixel++, offset += CHANNELS) {
    if (data[offset + ALPHA] !== OPAQUE) {
      indexes[pixel] = transparentIndex;
    }
  }
  return indexes;
};

export const exportGif = async (options: ExportGifOptions): Promise<Blob> => {
  const { frames } = options;
  const first = frames[0];
  const firstLayers = first === undefined ? [] : await options.loadLayers(first);
  const reference = firstLayers[0];
  if (reference === undefined) {
    throw new Error('Nothing to export');
  }
  const size = fitSize(reference, options.maxSize);
  const { context } = createCompositeCanvas(size);
  context.imageSmoothingEnabled = false;
  const render = async (frame: FrameOutput, layers?: ImageBitmap[]): Promise<Uint8ClampedArray | null> => {
    const resolved = layers ?? await options.loadLayers(frame);
    if (resolved.length === 0) {
      return null;
    }
    drawComposite(context, resolved, frame, options.productLabel, size);
    return context.getImageData(0, 0, size.width, size.height).data;
  };

  const samples: Uint8ClampedArray[] = [];
  for (const index of sampleIndexes(frames.length)) {
    const frame = frames[index];
    const rendered = frame === undefined ? null : await render(frame, index === 0 ? firstLayers : undefined);
    if (rendered !== null) {
      samples.push(rendered);
    }
  }
  const colors = quantize(concat(samples), MAX_COLORS - 1);
  const transparentIndex = colors.length;
  const palette: Palette = [ ...colors, [ 0, 0, 0 ] ];

  const encoder = GIFEncoder();
  let previous: Uint8ClampedArray | null = null;
  for (const [ index, frame ] of frames.entries()) {
    const data = await render(frame, index === 0 ? firstLayers : undefined);
    if (data === null) {
      continue;
    }
    const delay = index === frames.length - 1 ? options.lastDelayMs : options.delayMs;
    if (previous === null) {
      encoder.writeFrame(applyPalette(data, colors), size.width, size.height, { palette, delay, repeat: 0 });
    } else {
      const diff = differential(data, previous);
      encoder.writeFrame(indexWithTransparency(diff, colors, transparentIndex), size.width, size.height, {
        delay,
        transparent: true,
        transparentIndex,
        dispose: DISPOSE_KEEP,
      });
    }
    previous = data;
    options.onProgress?.(index + 1, frames.length);
    await yieldToBrowser();
  }
  encoder.finish();
  return new Blob([ encoder.bytes() as BlobPart ], { type: 'image/gif' });
};
